import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@supabase/auth-helpers-react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, ChevronDownIcon, MapPinIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { supabase, getCategoriesGroupedByType } from '../lib/supabase';
import { calculateDistance } from '../lib/distance';
import CampCard from '../components/CampCard';
import CampMap from '../components/CampMap';
import { Skeleton } from '../components/ui/Skeleton';
import FocusAreaChips from '../components/ui/FocusAreaChips';

// Lazy load admin overlay to prevent MDX editor from being bundled in SSR
const AdminCampOverlay = lazy(() => import('../components/AdminCampOverlay'));
import AdvancedFilters from '../components/ui/AdvancedFilters';
import type { Camp } from '../types/database';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, LOCATION_ZOOM, SEARCH_PLACEHOLDER } from '../lib/constants';
import SEO from '../components/SEO';
import ClientOnly from '../components/ClientOnly';
import { api } from '../lib/api';
import { track } from '../lib/track';
import { trackEvent } from '../lib/posthog';
import { searchZipCodes, getZipCodeInfo, type ZipCodeInfo } from '../lib/zipCodeLookup';
import { searchStates, getStateCenter } from '../lib/stateCenters';
import { getCityCoordinates } from '../lib/geocoding';
import { useMapZoom } from '../hooks/useMapZoom';
import { useMapCamps } from '../hooks/useMapCamps';

import 'leaflet/dist/leaflet.css';

interface LocationInfo {
  coords: [number, number];
  city: string;
  state: string;
}



export default function HomePage() {
  console.log('🔍 [HomePage] Rendering HomePage');
  console.log('🔍 [HOMEPAGE] Component rendering...');
  const session = useSession();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDropdownIndex, setSelectedDropdownIndex] = useState<number>(-1);
  const [userLocation, setUserLocation] = useState<LocationInfo | null>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>([DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng]);
  const [radius, setRadius] = useState<number | null>(10);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isMapOpen, setIsMapOpen] = useState(false); // Mobile map overlay toggle
  const [isFiltersOpen, setIsFiltersOpen] = useState(false); // Mobile filters toggle
  
  // Use shared hook for map zoom calculation
  const mapZoom = useMapZoom(radius);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  
  // Edit overlay state
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [isEditOverlayOpen, setIsEditOverlayOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  
  // Admin check
  const { data: isAdmin } = useQuery(
    ['is-admin', session?.user?.id],
    async () => {
      if (!session?.user?.id) return false;
      
      const { data, error } = await supabase
        .rpc('check_is_admin', {
          user_id: session.user.id
        });
      
      if (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
      
      return !!data;
    },
    {
      enabled: !!session?.user?.id,
      staleTime: 30000, // Cache for 30 seconds
      cacheTime: 60000, // Keep in cache for 1 minute
      retry: false
    }
  );

  // Only fire search_performed when user has changed search/filters, not on initial load
  const searchPerformedInitialLoadRef = useRef(false);

  // Fetch camps with categories - NEW OPTIMIZED VERSION
  const { data: camps = [], isLoading, error: campsError, refetch } = useQuery(
    ['camps', debouncedSearchQuery, selectedCategories, radius, mapCenter, userLocation, currentPage, pageSize],
    async () => {
      console.log('🔍 [CAMPS] Camps query triggered with key:', ['camps', debouncedSearchQuery, selectedCategories, radius, mapCenter, userLocation, currentPage, pageSize]);
      console.log('🔍 [CAMPS] Fetching filtered camps with new API...');
      console.log('🔍 [CAMPS] Selected categories being passed to API:', selectedCategories);
      
      // Determine if we should use location-based filtering
      const shouldUseLocation = userLocation && radius && mapCenter && 
        mapCenter[0] !== DEFAULT_MAP_CENTER.lat && 
        mapCenter[1] !== DEFAULT_MAP_CENTER.lng;
      
      try {
        const result = await api.camps.fetchFilteredCamps({
          search: debouncedSearchQuery,
          categories: selectedCategories,
          radius: shouldUseLocation ? radius : null,
          center: shouldUseLocation ? mapCenter : null,
          page: currentPage,
          pageSize: pageSize,
          sortBy: 'name' // We'll handle distance sorting in the response if needed
        });
        
        console.log('🔍 [CAMPS] Fetched filtered camps:', result.length);
        console.log('🔍 [CAMPS] First few camps:', result.slice(0, 3).map(c => c.name));
        return result;
      } catch (error) {
        console.error('🔍 [CAMPS] Error fetching camps:', error);
        throw error;
      }
    },
    {
      staleTime: 1000 * 60 * 2, // 2 minutes (reduced from 5)
      retry: 1,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (!searchPerformedInitialLoadRef.current) {
          searchPerformedInitialLoadRef.current = true;
          return;
        }
        track('search_performed', {
          query: debouncedSearchQuery || undefined,
          location: userLocation ? `${userLocation.city}, ${userLocation.state}` : undefined,
          radius_miles: radius ?? undefined,
          filters: selectedCategories.length ? { categories: selectedCategories } : undefined,
          results_count: data?.length ?? 0,
        });
      },
      onError: (error) => {
        console.error('🔍 [CAMPS] Query error:', error);
      },
    }
  );

  // Use shared hook to fetch ALL camps for the map (without pagination)
  const { data: allCampsForMap = [] } = useMapCamps({
    searchQuery: debouncedSearchQuery,
    selectedCategories,
    radius,
    mapCenter,
    userLocation,
    queryKeyPrefix: 'camps-for-map'
  });

  // Get total count for pagination
  const countQueryKey = ['camps-count', debouncedSearchQuery, selectedCategories, radius, mapCenter, userLocation?.coords];
  console.log('🔍 [CAMPS] Count query key changed:', countQueryKey);
  
  const { data: totalCampsCount = 0, refetch: refetchCount, isFetching: isCountFetching } = useQuery(
    countQueryKey,
    async () => {
      console.log('🔍 [CAMPS] Count query triggered with key:', countQueryKey);
      console.log('🔍 [CAMPS] Count query params:', {
        search: debouncedSearchQuery,
        categories: selectedCategories,
        radius,
        mapCenter,
        userLocation: userLocation?.coords
      });
      
      // Determine if we should use location-based filtering
      const shouldUseLocation = userLocation && radius && mapCenter && 
        mapCenter[0] !== DEFAULT_MAP_CENTER.lat && 
        mapCenter[1] !== DEFAULT_MAP_CENTER.lng;
      
      const count = await api.camps.getFilteredCampsCount({
        search: debouncedSearchQuery,
        categories: selectedCategories,
        radius: shouldUseLocation ? radius : null,
        center: shouldUseLocation ? mapCenter : null,
      });
      
      console.log('🔍 [CAMPS] Total count for filters:', count);
      return count;
    },
    {
      staleTime: 0, // Always refetch when query key changes
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch grouped categories
  const { data: groupedCategories } = useQuery(['camp-categories-grouped'], async () => {
    return await getCategoriesGroupedByType();
  });

  // Edit handler
  const handleEditCamp = (camp: Camp) => {
    setSelectedCamp(camp);
    setIsEditOverlayOpen(true);
  };

  const handleEditSave = () => {
    setIsEditOverlayOpen(false);
    setSelectedCamp(null);
    refetch(); // Refresh the camps list
  };

  const handleDeleteCamp = () => {
    if (!selectedCamp) return;
    setIsEditOverlayOpen(false);
    setSelectedCamp(null);
    refetch(); // Refresh the camps list
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Wrapper function to reset pagination when categories change
  const handleCategoryChange = (categories: string[]) => {
    setSelectedCategories(categories);
    setCurrentPage(1); // Reset to first page when categories change
  };

  // Debounce search query to prevent excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    console.log('🔍 [PAGINATION] Filters changed, resetting to page 1');
    console.log('🔍 [PAGINATION] Filter changes:', {
      debouncedSearchQuery,
      selectedCategories: selectedCategories.length,
      radius,
      mapCenter
    });
    console.log('🔍 [PAGINATION] Selected categories:', selectedCategories);
    console.log('🔍 [PAGINATION] Radius changed to:', radius);
    console.log('🔍 [PAGINATION] This useEffect should trigger React Query refetch');
    setCurrentPage(1);
    
    // Force refetch of count when any filter changes
    console.log('🔍 [PAGINATION] Forcing count refetch due to filter changes');
    refetchCount();
  }, [debouncedSearchQuery, selectedCategories, radius, mapCenter]);

  // Additional effect to handle edge cases
  useEffect(() => {
    if (totalCampsCount > 0 && totalCampsCount <= pageSize) {
      console.log('🔍 [PAGINATION] Total camps <= page size, ensuring page 1');
      setCurrentPage(1);
    }
  }, [totalCampsCount, pageSize]);

  // Debug effect for selectedCategories changes
  useEffect(() => {
    console.log('🔍 [CATEGORIES] selectedCategories changed:', selectedCategories);
    console.log('🔍 [CATEGORIES] selectedCategories length:', selectedCategories.length);
    console.log('🔍 [CATEGORIES] selectedCategories details:', selectedCategories.map(id => ({ id, type: typeof id })));
    console.log('🔍 [CATEGORIES] This should trigger a new API call with these categories');
  }, [selectedCategories]);

  // Ensure count stays in sync with camps data
  useEffect(() => {
    if (camps.length > 0) {
      console.log('🔍 [PAGINATION] Camps data changed, ensuring count is up to date');
      refetchCount();
    }
  }, [camps]);

  // Get user location
  useEffect(() => {
    console.log('🔍 [LOCATION] Starting location detection...');
    console.log('🔍 [LOCATION] Navigator geolocation available:', 'geolocation' in navigator);
    
    if (!('geolocation' in navigator)) {
      console.log('🔍 [LOCATION] Geolocation not supported');
      handleDefaultView('Geolocation is not supported by your browser.');
      return;
    }

    console.log('🔍 [LOCATION] Requesting user location with 30s timeout...');
    console.log('🔍 [LOCATION] Starting geolocation request at:', new Date().toISOString());
    setIsRequestingLocation(true);

    // Set a timeout to detect if geolocation is taking too long
    const timeoutId = setTimeout(() => {
      console.log('🔍 [LOCATION] Geolocation timeout detected - taking longer than expected');
      setIsRequestingLocation(false);
      setIsMapLoading(false);
    }, 35000); // 35 seconds timeout

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        clearTimeout(timeoutId);
        console.log('🔍 [LOCATION] Got position:', position.coords);
        console.log('🔍 [LOCATION] Position accuracy:', position.coords.accuracy, 'meters');
        console.log('🔍 [LOCATION] Position timestamp:', new Date(position.timestamp).toISOString());
        
        const { latitude, longitude } = position.coords;
        try {
          console.log('🔍 [LOCATION] Fetching city data...');
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
          );
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          console.log('🔍 [LOCATION] City data response:', data);
          
          const updates = {
            coords: [latitude, longitude] as [number, number],
            city: data.city || 'your location',
            state: data.principalSubdivision || ''
          };
          
          console.log('🔍 [LOCATION] Setting location:', updates);
          console.log('🔍 [LOCATION] Map center will be set to:', updates.coords);
          console.log('🔍 [LOCATION] Radius will be set to: 10');
          setUserLocation(updates);
          setMapCenter(updates.coords);
          setRadius(10); // Default to 10 miles, zoom will be updated automatically by useEffect
          setLocationError(null);
          setCurrentPage(1); // Reset to first page when location changes
        } catch (error) {
          console.error('🔍 [LOCATION] Error getting city:', error);
          handleDefaultView('Unable to determine your city. Showing all camps across the US.');
        } finally {
          setIsRequestingLocation(false);
          setIsMapLoading(false);
        }
      },

      (error) => {
        clearTimeout(timeoutId);
        console.log('🔍 [LOCATION] Geolocation error occurred:', error);
        console.log('🔍 [LOCATION] Error code:', error.code);
        console.log('🔍 [LOCATION] Error message:', error.message);
        
        if (error.code === error.PERMISSION_DENIED) {
          console.log('🔍 [LOCATION] Permission denied by user');
          handleDefaultView('Location access denied. Please allow location access in your browser settings to see camps near you.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          console.log('🔍 [LOCATION] Position unavailable');
          handleDefaultView('Unable to determine your location. Your device may not have GPS or location services enabled.');
        } else if (error.code === error.TIMEOUT) {
          console.log('🔍 [LOCATION] Timeout occurred at:', new Date().toISOString());
          console.log('🔍 [LOCATION] Timeout error details:', error);
          handleDefaultView('Location request timed out after 30 seconds. Your device may be taking longer than expected to get GPS coordinates. Showing all camps across the US.');
        } else {
          console.warn('🔍 [LOCATION] Other geolocation error:', error);
          handleDefaultView('Unable to get your location. Please check your browser settings and try again.');
        }
        setIsRequestingLocation(false);
        setIsMapLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 60000 // Accept cached position up to 1 minute old
      }
    );
  }, []);

  // Helper to set default view state (USA view)
  const handleDefaultView = (message: string) => {
    // Default to USA center (already DEFAULT_MAP_CENTER)
    setUserLocation({
      coords: [DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng],
      city: 'United States',
      state: 'USA'
    });
    setMapCenter([DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng]);
    setMapZoom(DEFAULT_MAP_ZOOM);
    setRadius(null); // No radius for USA-wide view
    setLocationError(message);
    setIsRequestingLocation(false);
    setIsMapLoading(false);
    setCurrentPage(1); // Reset to first page when view changes
  };

  // Function to get coordinates for a city/zip
  const getCityCoordinates = async (searchTerm: string) => {
    // First try to find the city in our database
    const [city, state] = searchTerm.split(',').map(s => s.trim());
    const matchingCamp = camps.find(camp => 
      camp.city.toLowerCase() === city.toLowerCase() &&
      camp.state.toLowerCase() === state.toLowerCase()
    );

    if (matchingCamp) {
      return { lat: matchingCamp.latitude, lng: matchingCamp.longitude };
    }

    // Fall back to Google Geocoding API if not found in database
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchTerm)}&components=country:US&key=${import.meta.env.VITE_GOOGLE_API_KEY}`
      );
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        return { lat, lng };
      }
      return null;
    } catch (error) {
      console.error('Error getting city coordinates:', error);
      return null;
    }
  };

  // Get unique cities and categories for SEO
  const uniqueCities = Array.from(new Set(camps.map(camp => `${camp.city}, ${camp.state}`)))
    .sort();
  const uniqueCategories = Array.from(new Set(camps.flatMap(camp => 
    camp.camp_categories?.map((cat: any) => cat.name) || []
  ).filter(Boolean)))
    .sort();

  // Prepare structured data for the homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "CampWhiz",
    "url": "https://campwhiz.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://campwhiz.com/camps?search={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "description": "Find and compare summer camps across the United States. Search by location, age, and activities.",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "itemOffered": camps.map(camp => ({
        "@type": "Camp",
        "name": camp.name,
        "description": camp.description,
        "url": `https://campwhiz.com/camps/${camp.slug}`,
        "address": {
          "@type": "PostalAddress",
          "addressLocality": camp.city,
          "addressRegion": camp.state
        }
      }))
    }
  };

  // Fetch all unique cities from database for search suggestions
  const { data: allCities = [] } = useQuery(
    ['all-cities'],
    async () => {
      const { data, error } = await supabase
        .from('camps')
        .select('city, state')
        .eq('active', true)
        .not('city', 'is', null)
        .not('state', 'is', null);
      
      if (error) {
        console.error('Error fetching cities:', error);
        return [];
      }
      
      // Get unique city/state combinations
      const uniqueCitiesSet = new Set<string>();
      (data || []).forEach((camp: any) => {
        if (camp.city && camp.state) {
          uniqueCitiesSet.add(`${camp.city}, ${camp.state}`);
        }
      });
      
      return Array.from(uniqueCitiesSet).sort();
    },
    {
      staleTime: 1000 * 60 * 10, // Cache for 10 minutes
      cacheTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    }
  );

  // Get search suggestions
  const getSearchSuggestions = () => {
    const query = searchQuery.trim();
    
    // Only show suggestions after 2 characters
    if (query.length < 2) return { camps: [], cities: [], zipCodes: [], states: [] };
    
    const queryLower = query.toLowerCase();
    const isNumericQuery = /^\d+$/.test(query);
    
    // Find matching cities from all cities in database (limit to 5, but only if not numeric query)
    const matchingCities = !isNumericQuery ? (allCities || [])
      .filter((city: string) => city.toLowerCase().includes(queryLower))
      .slice(0, 5) : [];

    // Find matching states (limit to 3, but only if not numeric query)
    const matchingStates = !isNumericQuery ? searchStates(query).slice(0, 3) : [];

    // Find matching camps (limit to 8, but only if not numeric query)
    const matchingCamps = !isNumericQuery ? camps.filter(camp => 
      camp.name?.toLowerCase().includes(queryLower) ||
      camp.city?.toLowerCase().includes(queryLower) ||
      camp.description?.toLowerCase().includes(queryLower)
    ).slice(0, 8) : [];

    // Find matching ZIP codes (limit to 5, prioritize if numeric)
    let matchingZipCodes: ZipCodeInfo[] = [];
    try {
      matchingZipCodes = searchZipCodes(query, 5);
    } catch (error) {
      console.error('❌ [HomePage] Error in searchZipCodes:', error);
      matchingZipCodes = [];
    }

    return { camps: matchingCamps, cities: matchingCities, zipCodes: matchingZipCodes, states: matchingStates };
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    const shouldShow = value.trim().length >= 2;
    setShowDropdown(shouldShow);
    setSelectedDropdownIndex(-1); // Reset selection when typing
    setCurrentPage(1); // Reset to first page when search changes
    
    // Track search input (debounced to avoid excessive tracking)
    if (value.trim()) {
      trackEvent('search_input_entered', {
        search_query: value,
        search_length: value.length,
        has_categories: selectedCategories.length > 0,
        has_location: !!userLocation
      });
    }
  };

  // Handle city selection
  const handleCitySelect = async (city: string) => {
    setSearchQuery('');
    setShowDropdown(false);
    setSelectedDropdownIndex(-1);
    setIsRequestingLocation(false);
    setIsMapLoading(true);

    // Track city selection
    trackEvent('city_selected', {
      city: city,
      city_name: city.split(',')[0].trim(),
      state: city.split(',')[1].trim(),
      search_query: searchQuery
    });

    try {
      // Use geocodeAddress from geocoding library for consistent behavior
      const { geocodeAddress } = await import('../lib/geocoding');
      const [cityName, stateName] = city.split(',').map(s => s.trim());
      const result = await geocodeAddress(undefined, cityName, stateName, undefined);
      
      if (result && result.accuracy !== 'failed') {
        const newCenter: [number, number] = [result.latitude, result.longitude];
        setMapCenter(newCenter);
        setRadius(10); // Zoom will be updated automatically by useEffect
        setUserLocation({
          coords: newCenter,
          city: cityName,
          state: stateName
        });
        setLocationError(null);
      } else {
        console.error('Could not get coordinates for city:', city);
        setLocationError('Could not find coordinates for this city.');
      }
    } catch (error) {
      console.error('Error updating map location:', error);
      setLocationError('Error finding this location.');
    } finally {
      setIsMapLoading(false);
    }
  };

  // Handle ZIP code selection
  const handleZipCodeSelect = (zipInfo: { zip: string; city: string; state: string; latitude: number; longitude: number }) => {
    setSearchQuery('');
    setShowDropdown(false);
    setSelectedDropdownIndex(-1);
    const newCenter: [number, number] = [zipInfo.latitude, zipInfo.longitude];
    setMapCenter(newCenter);
    setRadius(10); // Zoom will be updated automatically by useEffect
    setUserLocation({
      coords: newCenter,
      city: zipInfo.city || '',
      state: zipInfo.state || ''
    });
    setLocationError(null);
  };

  // Handle state selection
  const handleStateSelect = (stateInfo: { name: string; lat: number; lng: number }) => {
    setSearchQuery('');
    setShowDropdown(false);
    setSelectedDropdownIndex(-1);
    const newCenter: [number, number] = [stateInfo.lat, stateInfo.lng];
    setMapCenter(newCenter);
    setRadius(100); // Zoom will be updated automatically by useEffect (9.5 for radius 100)
    setUserLocation({
      coords: newCenter,
      city: '',
      state: stateInfo.name
    });
    setLocationError(null);
  };

  // Handle camp selection from dropdown (only navigate on Enter, not click)
  const handleCampSelect = (camp: Camp, navigateToPage: boolean = false) => {
    if (navigateToPage) {
      // Navigate to camp page
      let slug = camp.slug;
      
      // Generate slug if it doesn't exist (same logic as CampCard)
      if (!slug && camp.name && camp.city && camp.state) {
        const stateSlug = camp.state.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        const citySlug = camp.city.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        const nameSlug = camp.name.toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .trim();
        slug = `${stateSlug}/${citySlug}/${nameSlug}`;
      }
      
      if (slug && slug.includes('/')) {
        navigate(`/camps/${slug}`);
      } else if (slug) {
        navigate(`/camps/${slug}`);
      } else {
        // Fallback to ID if we can't generate a slug
        navigate(`/camps/${camp.id}`);
      }
      setShowDropdown(false);
      setSelectedDropdownIndex(-1);
    } else {
      // Just update search query and map on click
      setSearchQuery(camp.name);
      setShowDropdown(false);
      setSelectedDropdownIndex(-1);
      setMapCenter([camp.latitude, camp.longitude]);
      setRadius(10); // Zoom will be updated automatically by useEffect
    }
  };

  // Handle radius change
  const handleRadiusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value ? Number(e.target.value) : null;
    setRadius(value);
    setCurrentPage(1); // Reset to first page when radius changes
    
    // Track radius change
    if (value) {
      trackEvent('search_radius_changed', {
        radius: value,
        previous_radius: radius,
        map_zoom: mapZoom,
        has_location: !!userLocation
      });
      
      // Adjust zoom level based on radius
      // Zoom will be updated automatically by useEffect based on radius
      // No need to set zoom here anymore
    }
  };

  // Handle map movement
  const handleMapMove = (newCenter: [number, number]) => {
    setMapCenter(newCenter);
    setCurrentPage(1); // Reset to first page when map moves
    // Only set radius if it's not already set and we have user location
    if (!radius && userLocation) {
      setRadius(30);
      // Don't set zoom here to preserve user's current zoom level
    }
  };

  // Since filtering is now handled at the database level, we can simplify this significantly
  // The camps array now contains only the camps that match our filters and pagination
  const filteredCamps = camps; // No more client-side filtering needed!
  
  // For distance-based sorting, we only need to sort the visible camps (much faster!)
  console.log('🔍 [SORTING] Starting sort with:', {
    userLocation: !!userLocation,
    radius,
    mapCenter,
    DEFAULT_MAP_CENTER,
    isDefaultMapCenter: mapCenter && mapCenter[0] === DEFAULT_MAP_CENTER.lat && mapCenter[1] === DEFAULT_MAP_CENTER.lng,
    campsCount: filteredCamps.length
  });
  
  // Test the first few camps to see their coordinates
  if (filteredCamps.length > 0) {
    console.log('🔍 [SORTING] First 3 camps coordinates:');
    filteredCamps.slice(0, 3).forEach((camp, index) => {
      console.log(`  ${index + 1}. ${camp.name}: lat=${camp.latitude}, lng=${camp.longitude}`);
    });
  }
  
  console.log('🔍 [SORTING] About to start sorting...');
  const sortedCamps = [...filteredCamps].sort((a, b) => {
    // First priority: distance (closer is better) - only if we have location and radius
    console.log('🔍 [SORTING] Checking distance sort conditions...');
    if (userLocation && radius && mapCenter) {
      console.log('🔍 [SORTING] Basic conditions met, checking map center...');
      if (mapCenter[0] !== DEFAULT_MAP_CENTER.lat || mapCenter[1] !== DEFAULT_MAP_CENTER.lng) {
        console.log('🔍 [SORTING] Map center is not default, proceeding with distance sort');
        
        const distanceA = calculateDistance(
          mapCenter[0],
          mapCenter[1],
          a.latitude,
          a.longitude
        );
        const distanceB = calculateDistance(
          mapCenter[0],
          mapCenter[1],
          b.latitude,
          b.longitude
        );
        
        // Debug logging for first few camps to see distances
        if (filteredCamps.indexOf(a) < 3) {
          console.log(`🔍 [DISTANCE] ${a.name} (${a.city}, ${a.state}): ${distanceA.toFixed(2)} miles`);
        }
        
        console.log(`🔍 [SORTING] Comparing ${a.name} (${distanceA.toFixed(2)} mi) vs ${b.name} (${distanceB.toFixed(2)} mi)`);
        
        if (Math.abs(distanceA - distanceB) > 0.1) { // If distance difference is significant (>0.1 miles)
          const result = distanceA - distanceB; // Closer distance first
          console.log(`🔍 [SORTING] Distance difference significant, returning: ${result}`);
          return result;
        } else {
          console.log(`🔍 [SORTING] Distance difference not significant, moving to score comparison`);
        }
      } else {
        console.log('🔍 [SORTING] Map center is still default, skipping distance sort');
      }
    } else {
      console.log('🔍 [SORTING] Basic conditions not met, skipping distance sort');
    }
    
    // Second priority: enrichment score (higher is better)
    const scoreA = a.score || 0;
    const scoreB = b.score || 0;
    
    if (scoreA !== scoreB) {
      const result = scoreB - scoreA; // Higher score first
      console.log(`🔍 [SORTING] Score difference: ${scoreA} vs ${scoreB}, returning: ${result}`);
      return result;
    }
    
    // Third priority: name (alphabetical as tiebreaker)
    const result = a.name.localeCompare(b.name);
    console.log(`🔍 [SORTING] Using name comparison: ${a.name} vs ${b.name}, returning: ${result}`);
    return result;
  });
  
  console.log('🔍 [SORTING] Final order:', sortedCamps.map((camp, index) => 
    `${index + 1}. ${camp.name} (${camp.city}, ${camp.state})`
  ));

  // Force re-sort when camps or location changes
  useEffect(() => {
    console.log('🔍 [FORCE-SORT] useEffect triggered - camps or location changed');
    console.log('🔍 [FORCE-SORT] Current state:', {
      campsLength: camps.length,
      userLocation: !!userLocation,
      radius,
      mapCenter
    });
    
    // Force count refetch when camps change (this ensures count stays in sync)
    if (camps.length > 0) {
      console.log('🔍 [FORCE-SORT] Forcing count refetch due to camps change');
      refetchCount();
    }
  }, [camps, userLocation, radius, mapCenter, refetchCount]);

  // Pagination is now handled by the API, so we just use the camps directly
  const paginatedCamps = sortedCamps; // No more slicing needed!
  
  // Update pagination variables to reflect the new approach
  const totalCamps = totalCampsCount; // Use the count from API instead of local array length
  const totalPages = Math.ceil(totalCamps / pageSize);
  // Note: startIndex and endIndex are no longer needed since API handles pagination

  // Debug logging for pagination
  console.log('🔍 [PAGINATION] Current state:', {
    currentPage,
    pageSize,
    totalCamps,
    totalPages,
    totalCampsCount,
    campsLength: camps.length,
    isCountFetching
  });

  // Ensure current page is valid when total pages change
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      console.log('🔍 [PAGINATION] Current page exceeds total pages, resetting to 1');
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  // Debug logging for location and filtering
  console.log('🔍 [DEBUG] Current state:', {
    userLocation,
    mapCenter,
    radius,
    isRequestingLocation,
    locationError,
    totalCamps: totalCamps,
    sortedCamps: sortedCamps.length,
    paginatedCamps: paginatedCamps.length
  });

  // Get search suggestions
  const { camps: suggestedCamps, cities: suggestedCities, zipCodes: suggestedZipCodes, states: suggestedStates } = getSearchSuggestions();
  const hasSuggestions = suggestedCamps.length > 0 || suggestedCities.length > 0 || suggestedZipCodes.length > 0 || suggestedStates.length > 0;

  // Force re-render when location changes
  const locationKey = userLocation ? `${userLocation.coords[0]}-${userLocation.coords[1]}-${radius}` : 'no-location';
  
  return (
    <>
      <SEO
        title="Find Summer Camps Near You | CampWhiz"
        description="Discover and compare summer camps across the United States. Search by location, age, and activities. Find the perfect camp for your child today!"
        keywords={`summer camps, ${uniqueCities.join(', ')}, ${uniqueCategories.join(', ')}, camp finder, summer activities, children's camps`}
        url="/"
        structuredData={structuredData}
      />
      
      <div className="page-container" key={locationKey}>
        <div className="page-content">
          <section aria-labelledby="hero-heading" className="text-center mb-2 sm:mb-8 lg:mb-16">
            <h1 id="hero-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-gray-900 mb-4">
              Find the Perfect Summer Camp
            </h1>
            <div className="text-lg sm:text-xl text-gray-500 mb-4 sm:mb-6 lg:mb-8">
              <ClientOnly fallback={<span>Showing camps across the United States</span>}>
                {isRequestingLocation ? (
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                      <span>Requesting your location...</span>
                    </div>
                    <span className="text-sm text-gray-500">This may take up to 30 seconds</span>
                  </div>
                ) : userLocation ? (
                  <span>Camps near {userLocation.city}, {userLocation.state}</span>
                ) : locationError ? (
                  <div className="flex flex-col items-center gap-2">
                    <span>{locationError}</span>
                    <button
                      onClick={() => {
                        console.log('🔍 [LOCATION] Manual location trigger clicked');
                        setIsRequestingLocation(true);
                        setIsMapLoading(true);
                        // Re-trigger location detection
                        navigator.geolocation.getCurrentPosition(
                          async (position) => {
                            console.log('🔍 [LOCATION] Manual location detection got position:', position.coords);
                            const { latitude, longitude } = position.coords;
                            try {
                              const response = await fetch(
                                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
                              );
                              const data = await response.json();
                              const updates = {
                                coords: [latitude, longitude] as [number, number],
                                city: data.city || 'your location',
                                state: data.principalSubdivision || ''
                              };
                              setUserLocation(updates);
                              setMapCenter(updates.coords);
                              setRadius(10); // Default to 10 miles, zoom will be updated automatically by useEffect
                              setLocationError(null);
                              setCurrentPage(1);
                            } catch (error) {
                              console.error('🔍 [LOCATION] Manual location error:', error);
                              setLocationError('Failed to get location. Please try again.');
                            } finally {
                              setIsRequestingLocation(false);
                              setIsMapLoading(false);
                            }
                          },
                          (error) => {
                            console.error('🔍 [LOCATION] Manual location geolocation error:', error);
                            setLocationError('Location permission denied. Please allow location access.');
                            setIsRequestingLocation(false);
                            setIsMapLoading(false);
                          }
                        );
                      }}
                      className="btn btn-primary btn-sm"
                    >
                      Try Again
                    </button>
                  </div>
                ) : (
                  <span>Camps across the United States</span>
                )}
              </ClientOnly>
            </div>
            


            {/* Smart Search */}
            <div className="max-w-2xl mx-auto mb-0 lg:mb-3">
              {/* Search Input and Radius Row - Desktop: same row, Mobile: stacked */}
              <div className="flex flex-col lg:flex-row items-stretch gap-2 sm:gap-3">
                {/* Search Input */}
                <div className="relative flex-1 z-[1] lg:z-[1000]">
                  <label htmlFor="camp-search" className="sr-only">Search camps or enter a city</label>
                  <input
                    id="camp-search"
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    name="camp-search"
                    data-form-type="other"
                    onFocus={() => {
                      if (searchQuery.trim().length >= 2) {
                        setShowDropdown(true);
                      }
                    }}
                    onKeyDown={(e) => {
                      const { camps: suggestedCamps, cities: suggestedCities, zipCodes: suggestedZipCodes, states: suggestedStates } = getSearchSuggestions();
                      
                      // Build flat list of all dropdown items for navigation
                      const allItems: Array<{ type: 'zip' | 'state' | 'city' | 'camp'; data: any }> = [
                        ...suggestedZipCodes.map(z => ({ type: 'zip' as const, data: z })),
                        ...suggestedStates.map(s => ({ type: 'state' as const, data: s })),
                        ...suggestedCities.map(c => ({ type: 'city' as const, data: c })),
                        ...suggestedCamps.map(c => ({ type: 'camp' as const, data: c }))
                      ];
                      
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        if (!showDropdown && searchQuery.trim().length >= 2) {
                          setShowDropdown(true);
                        }
                        setSelectedDropdownIndex(prev => 
                          prev < allItems.length - 1 ? prev + 1 : 0
                        );
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setSelectedDropdownIndex(prev => 
                          prev > 0 ? prev - 1 : allItems.length - 1
                        );
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (selectedDropdownIndex >= 0 && selectedDropdownIndex < allItems.length) {
                          const selectedItem = allItems[selectedDropdownIndex];
                          if (selectedItem.type === 'zip') {
                            handleZipCodeSelect(selectedItem.data);
                          } else if (selectedItem.type === 'state') {
                            handleStateSelect(selectedItem.data);
                          } else if (selectedItem.type === 'city') {
                            handleCitySelect(selectedItem.data);
                          } else if (selectedItem.type === 'camp') {
                            handleCampSelect(selectedItem.data, true); // Navigate to page
                          }
                        } else if (searchQuery.trim().length === 5 && /^\d{5}$/.test(searchQuery.trim())) {
                          // Fallback: auto-navigate on Enter for complete 5-digit ZIP code
                          const zipInfo = getZipCodeInfo(searchQuery.trim());
                          if (zipInfo) {
                            handleZipCodeSelect(zipInfo);
                          }
                        }
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setShowDropdown(false);
                        setSelectedDropdownIndex(-1);
                      }
                    }}
                    onBlur={() => {
                      // Delay closing to allow clicks on dropdown items
                      setTimeout(() => {
                        // Only close if not clicking on dropdown
                        if (!document.activeElement?.closest('[role="listbox"]')) {
                          setShowDropdown(false);
                          setSelectedDropdownIndex(-1);
                        }
                      }, 200);
                    }}
                    placeholder="Search camps, cities, states, or ZIP codes..."
                    className="form-field w-full px-4 py-3 pl-10 pr-4 text-gray-900 placeholder-gray-500 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none"
                    aria-expanded={showDropdown}
                    aria-controls="search-results"
                    aria-label="Search camps, cities, states, or ZIP codes"
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" aria-hidden="true" />

                {/* Smart Dropdown */}
                {showDropdown && searchQuery.trim().length >= 2 && hasSuggestions && (() => {
                  // Build flat list of all dropdown items for keyboard navigation
                  const allItems: Array<{ type: 'zip' | 'state' | 'city' | 'camp'; data: any; index: number }> = [];
                  let itemIndex = 0;
                  
                  suggestedZipCodes.forEach(zip => {
                    allItems.push({ type: 'zip', data: zip, index: itemIndex++ });
                  });
                  suggestedStates.forEach(state => {
                    allItems.push({ type: 'state', data: state, index: itemIndex++ });
                  });
                  suggestedCities.forEach(city => {
                    allItems.push({ type: 'city', data: city, index: itemIndex++ });
                  });
                  suggestedCamps.forEach(camp => {
                    allItems.push({ type: 'camp', data: camp, index: itemIndex++ });
                  });

                  const getItemIndex = (type: 'zip' | 'state' | 'city' | 'camp', data: any): number => {
                    return allItems.findIndex(item => item.type === type && JSON.stringify(item.data) === JSON.stringify(data));
                  };

                  return (
                    <div 
                      id="search-results"
                      className="absolute w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-auto divide-y divide-gray-100 z-[2] lg:z-[10001]"
                      role="listbox"
                      onMouseDown={(e) => e.preventDefault()} // Prevent input blur on click
                      style={{ 
                        position: 'absolute', 
                        top: '100%', 
                        left: 0, 
                        right: 0
                      }}
                    >
                      {/* ZIP Codes Section */}
                      {suggestedZipCodes.length > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                            ZIP Codes
                          </div>
                          {suggestedZipCodes.map((zipInfo) => {
                            const itemIndex = getItemIndex('zip', zipInfo);
                            const isSelected = selectedDropdownIndex === itemIndex;
                            return (
                              <button
                                key={zipInfo.zip}
                                onClick={() => handleZipCodeSelect(zipInfo)}
                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-gray-100' : ''}`}
                                role="option"
                                aria-selected={isSelected}
                              >
                                <div className="flex-1">
                                  <span className="font-medium">ZIP {zipInfo.zip}</span>
                                </div>
                                <div className="text-sm text-gray-500">View area</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* States Section */}
                      {suggestedStates.length > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                            States
                          </div>
                          {suggestedStates.map((stateInfo) => {
                            const itemIndex = getItemIndex('state', stateInfo);
                            const isSelected = selectedDropdownIndex === itemIndex;
                            return (
                              <button
                                key={stateInfo.name}
                                onClick={() => handleStateSelect(stateInfo)}
                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center justify-between ${isSelected ? 'bg-gray-100' : ''}`}
                                role="option"
                                aria-selected={isSelected}
                              >
                                <div className="flex-1">
                                  <span className="font-medium">{stateInfo.name}</span>
                                </div>
                                <div className="text-sm text-gray-500">View area</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Cities Section */}
                      {suggestedCities.length > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                            Cities
                          </div>
                          {suggestedCities.map((city) => {
                            const itemIndex = getItemIndex('city', city);
                            const isSelected = selectedDropdownIndex === itemIndex;
                            return (
                              <button
                                key={city}
                                onClick={() => handleCitySelect(city)}
                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 flex items-center gap-2 ${isSelected ? 'bg-gray-100' : ''}`}
                                role="option"
                                aria-selected={isSelected}
                              >
                                <div className="flex-1">
                                  <span className="font-medium">{city}</span>
                                </div>
                                <div className="text-sm text-gray-500">View area</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Camps Section */}
                      {suggestedCamps.length > 0 && (
                        <div>
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                            Camps
                          </div>
                          {suggestedCamps.map((camp) => {
                            const itemIndex = getItemIndex('camp', camp);
                            const isSelected = selectedDropdownIndex === itemIndex;
                            return (
                              <button
                                key={camp.id}
                                onClick={() => handleCampSelect(camp)}
                                className={`w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${isSelected ? 'bg-gray-100' : ''}`}
                                role="option"
                                aria-selected={isSelected}
                              >
                                <div className="font-medium line-clamp-1">{camp.name}</div>
                                <div className="text-sm text-gray-500">{camp.city}, {camp.state}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* No Results */}
                      {!suggestedZipCodes.length && !suggestedStates.length && !suggestedCities.length && !suggestedCamps.length && (
                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                          No matches found
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

                {/* Radius Dropdown */}
                <div className="relative w-full lg:w-32 flex-shrink-0">
                  <label htmlFor="radius" className="sr-only">Search radius</label>
                  <select
                    id="radius"
                    value={radius || ''}
                    onChange={handleRadiusChange}
                    className="form-field w-full px-3 py-3 pr-8 rounded-lg border border-gray-300 shadow-sm focus:outline-none appearance-none bg-white text-gray-900"
                    aria-label="Select search radius"
                  >
                    {!userLocation && mapCenter && mapCenter[0] === DEFAULT_MAP_CENTER.lat && mapCenter[1] === DEFAULT_MAP_CENTER.lng ? (
                      <option value="">Select...</option>
                    ) : null}
                    <option value={10}>10 miles</option>
                    <option value={20}>20 miles</option>
                    <option value={50}>50 miles</option>
                    <option value={100}>100 miles</option>
                  </select>
                  <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
              </div>

              {/* Mobile Filter Toggle Button - Below search/radius row */}
              <div className="lg:hidden mt-2">
                <button
                  onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                  className="btn btn-secondary w-full flex items-center justify-center gap-2 min-h-[44px]"
                  aria-label="Toggle filters"
                >
                  <FunnelIcon className="w-5 h-5" />
                  Filters
                  {selectedCategories.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                      {selectedCategories.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* Focus Area Chips and Advanced Filters */}
            <div className={`max-w-4xl mx-auto ${isFiltersOpen ? 'mb-2 sm:mb-3 mt-2 sm:mt-3' : 'mb-0 mt-0'} lg:mb-3 lg:mt-3`}>
              <ClientOnly fallback={
                <div className="flex flex-wrap justify-center gap-2">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className="h-8 bg-gray-200 rounded-full animate-pulse w-20"></div>
                  ))}
                </div>
              }>
                {/* Filters - Hidden on mobile unless open */}
                <div className={`lg:block ${isFiltersOpen ? 'block' : 'hidden'}`}>
                  <div className="flex flex-wrap justify-center gap-2">
                    <FocusAreaChips
                      selectedCategories={selectedCategories}
                      onCategoryChange={handleCategoryChange}
                      className=""
                    />
                    
                    <AdvancedFilters
                      selectedCategories={selectedCategories}
                      onCategoryChange={handleCategoryChange}
                      className=""
                    />
                  </div>
                </div>
              </ClientOnly>
            </div>
          </section>

          {/* Map Section - Hidden on mobile, shown with toggle button */}
          <div className="lg:hidden mb-2 sm:mb-3">
            <button
              onClick={() => setIsMapOpen(true)}
              className="btn btn-primary w-full flex items-center justify-center gap-2 min-h-[44px]"
              aria-label="Show map"
            >
              <MapPinIcon className="w-5 h-5" />
              Show Map
            </button>
          </div>
          
          <ClientOnly fallback={
            <section aria-label="Camp locations map" className="relative mb-16 h-[400px] rounded-lg overflow-hidden shadow-lg bg-gray-100 flex items-center justify-center hidden lg:block">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-2" aria-hidden="true"></div>
                <p className="text-gray-600">Loading map...</p>
              </div>
            </section>
          }>
            <section aria-label="Camp locations map" className="relative mb-16 h-[400px] rounded-lg overflow-hidden shadow-lg z-10 hidden lg:block">
              {(isRequestingLocation || isMapLoading) && (
                <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center" role="status">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-2" aria-hidden="true"></div>
                    <p className="text-gray-600">
                      {isRequestingLocation ? 'Getting your location...' : 'Updating map...'}
                    </p>
                  </div>
                </div>
              )}
              <CampMap
                camps={allCampsForMap}
                center={mapCenter}
                zoom={mapZoom}
                onCampSelect={(camp) => handleCampSelect(camp, true)}
                onMapMove={handleMapMove}
                userLocation={userLocation?.coords || null}
                radius={radius}
              />
            </section>
          </ClientOnly>

          {/* Camp Cards */}
          <section aria-label="Camp listings" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading ? (
              <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} className="h-96" />
                ))}
              </div>
            ) : paginatedCamps.length > 0 ? (
              paginatedCamps.map((camp) => (
                <CampCard
                  key={camp.id}
                  camp={camp}
                  onFavoriteChange={() => refetch()}
                  onEdit={isAdmin ? handleEditCamp : undefined}
                />
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">No camps found matching your search criteria.</p>
              </div>
            )}
          </section>

          {/* Pagination */}
          {!isLoading && totalCamps > pageSize && (
            <div key={`pagination-${totalCamps}-${currentPage}`} className="mt-8 bg-white shadow-md rounded-lg overflow-hidden">
              <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>
                      {' '}to{' '}
                      {Math.min(currentPage * pageSize, totalCamps)}
                      {' '}of{' '}
                      <span className="font-medium">{totalCamps}</span>
                      {' '}results
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm text-gray-700">Per page:</label>
                      <div className="relative">
                        <select
                          value={pageSize}
                          onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                          className="border border-gray-300 rounded px-2 py-1 pr-8 text-sm appearance-none"
                        >
                          <option value={6}>6</option>
                          <option value={12}>12</option>
                          <option value={24}>24</option>
                          <option value={48}>48</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <svg className="h-4 w-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNumber = i + Math.max(1, currentPage - 2);
                        if (pageNumber > totalPages) return null;
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNumber === currentPage
                                ? 'z-10 bg-primary text-white border-primary'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Edit Camp Overlay */}
      {isEditOverlayOpen && (
        <Suspense fallback={<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div></div>}>
          <AdminCampOverlay
            camp={selectedCamp}
            isOpen={isEditOverlayOpen}
            isAdmin={isAdmin ?? false}
            onClose={() => setIsEditOverlayOpen(false)}
            onSave={handleEditSave}
            onDelete={handleDeleteCamp}
          />
        </Suspense>
      )}

      {/* Mobile Map Overlay - Full Screen */}
      {isMapOpen && (
        <div className="lg:hidden fixed inset-0 z-[200] bg-white">
          {/* Header with Close Button */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-white relative z-50">
            <h2 className="text-lg font-semibold text-gray-900">Map</h2>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsMapOpen(false);
              }}
              className="inline-flex items-center justify-center p-2 rounded-md text-primary hover:text-primary/80 hover:bg-gray-100 min-h-[44px] min-w-[44px] z-10"
              aria-label="Close map"
              type="button"
            >
              <XMarkIcon className="h-6 w-6 stroke-[2.5]" />
            </button>
          </div>
          
          {/* Full-Screen Map */}
          <div className="absolute inset-0 pt-[73px]">
            <ClientOnly fallback={
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading map...</p>
                </div>
              </div>
            }>
              {(isRequestingLocation || isMapLoading) && (
                <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center" role="status">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-2" aria-hidden="true"></div>
                    <p className="text-gray-600">
                      {isRequestingLocation ? 'Getting your location...' : 'Updating map...'}
                    </p>
                  </div>
                </div>
              )}
              <CampMap
                camps={allCampsForMap}
                center={mapCenter}
                zoom={mapZoom}
                onCampSelect={(camp) => {
                  handleCampSelect(camp, true);
                  setIsMapOpen(false); // Close map after selecting camp
                }}
                onMapMove={handleMapMove}
                userLocation={userLocation?.coords || null}
                radius={radius}
              />
            </ClientOnly>
          </div>
        </div>
      )}
    </>
  );
}